import { useEffect } from 'react';
import {
  useDispatch, useSelector,
} from 'react-redux';
import {
  Outlet, useParams,
} from 'react-router-dom';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import type { PublicMapRouteParams } from '~/_shared/constants/routes';
import { AuthorizationPageWrapperComponent } from '~/authentication/pageWrapper/authenticationPageWrapper.component';
import { MapPresentationalFormComponent } from '~/map/presentional/passwordForm/mapPresentationalFormComponent';
import { PrivateMapWarningComponent } from '~/map/presentional/privateMapWarning/privateMapWarning.component';
import type { AppState } from '~/store/app.store';
import { MapPresentationalView } from '~/store/frontendState/mapPresentational/mapPresentational.state';
import { mapReset } from '~/store/map/map.actionCreators';
import { mapInfoPresentationalFetchDataRequest } from '~/store/mapInfo/mapInfo.actionCreators';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePublicMapWebManifest } from './usePublicMapWebManifest.hook';

const mapViewSelector = (state: AppState) => state.frontendState.mapPresentational.view;
const isMapInfoLoadingSelector = (state: AppState) => state.map.mapInfo.isLoading;
const showWrongPasswordErrorSelector = (state: AppState) => state.frontendState.mapPresentational.showWrongPasswordError;

export const AuthorizedPresentationalMapRoute = () => {
  const dispatch = useDispatch();
  const { mapShareId = '' } = useParams<PublicMapRouteParams>();

  usePublicMapWebManifest(mapShareId);

  useEffect(() => {
    dispatch(mapReset());
    dispatch(mapInfoPresentationalFetchDataRequest(mapShareId));
  }, [dispatch, mapShareId]);

  const isLoading = useSelector(isMapInfoLoadingSelector);
  const mapView = useSelector(mapViewSelector);
  const showWrongPasswordError = useSelector(showWrongPasswordErrorSelector);
  const mapId = useMapIdSelector();

  const onPasswordFormSubmit = (password: string) => {
    dispatch(mapInfoPresentationalFetchDataRequest(mapShareId, password));
  };

  if (mapView === MapPresentationalView.Password) {
    return (
      <AuthorizationPageWrapperComponent>
        <MapPresentationalFormComponent
          isLoading={isLoading}
          onSubmit={onPasswordFormSubmit}
          showWrongPasswordError={showWrongPasswordError}
        />
      </AuthorizationPageWrapperComponent>
    );
  }

  if (mapView === MapPresentationalView.PrivateTokenMissing) {
    return (
      <AuthorizationPageWrapperComponent>
        <PrivateMapWarningComponent />
      </AuthorizationPageWrapperComponent>
    );
  }

  if (mapView === MapPresentationalView.Loading || !mapId) {
    return (
      <OverlayWrapperComponent minHeight="100%">
        <OverlayLoaderComponent />
      </OverlayWrapperComponent>
    );
  }

  return (
    <Outlet />
  );
};
